import React, { useRef, useEffect, useState, useMemo, memo, useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import FadeInContainer from "../motion/FadeInContainer";
import HighlightedText from "../../typography/HighlightedText";
import LoginTopSection from "../../../template/login/LoginTopSection";
import ShrinkingScrollExit from "../motion/ShrinkingScrollExit";
import { useResponsive } from "../../../hooks/useResponsive";
import brandMessages from "../../../data/brandMessages.json";

/**
 * ScrubbingVideoWithFullPage 컴포넌트
 * Login.js 전용 비디오 스크러빙 컴포넌트
 * 1/2 분할 레이아웃: 왼쪽 텍스트, 오른쪽 비디오
 *
 * Props:
 * @param {Array} sections - 브랜드 메시지 섹션 배열 [Required]
 *
 * Example usage:
 * <ScrubbingVideoWithFullPage sections={brandSections} />
 */
function ScrubbingVideoWithFullPage({ sections = [] }) {
	const theme = useTheme();
	const { isPC, isMobile, isMobileOnly } = useResponsive();
	const videoRef = useRef(null);
	const containerRef = useRef(null); // 상위 컨테이너 ref 추가
	const videoContainerRef = useRef(null); // 비디오 컨테이너 ref 추가

	// Login.js 전용 고정 값들
	// 🎯 Firebase Storage - 저화질부터 빠른 로딩
	const [videoSrc, setVideoSrc] = useState(
		"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fwriting_man_landing_low.mp4?alt=media"
	);
	const [retryCount, setRetryCount] = useState(0);
	const maxRetries = 3;

	// 비디오 프리로드 링크 추가 (페이지 로드 속도 향상)
	useEffect(() => {
		const link = document.createElement("link");
		link.rel = "preload";
		link.as = "video";
		link.href = videoSrc;
		link.type = "video/mp4";
		document.head.appendChild(link);

		return () => {
			document.head.removeChild(link);
		};
	}, [videoSrc]);
	const configDuration = brandMessages.totalDuration;

	// 🎯 동적 duration 관리 (압축된 비디오 대응)
	const [actualDuration, setActualDuration] = useState(configDuration);
	const [isVideoReady, setIsVideoReady] = useState(false);

	// 반응형 설정
	const responsiveConfig = useMemo(
		() => ({
			// 섹션 높이 설정
			section: {
				titleHeight: isPC ? "100vh" : "70vh", // 타이틀 섹션 높이
				contentHeight: isPC ? "50vh" : "35vh", // 일반 섹션 높이
				spacing: isPC ? 50 : 35, // 섹션 간격 (vh 단위)
			},
			// 비디오 트랜지션 설정 (모든 화면에서 스케일 트랜지션 사용)
			video: {
				initialScale: 2, // 모든 화면에서 2배에서 시작
			},
			// 메시지 섹션 너비 설정
			message: {
				sectionWidth: isPC ? 0.45 : 1.0, // PC: 45%, Mobile: 100%
			},
		}),
		[isPC]
	);

	// ShrinkingScrollExit 공통 옵션 객체화 (메모이제이션)
	const shrinkingScrollOptions = useMemo(
		() => ({
			triggerTop: isPC ? 320 : 240,
			triggerBottom: isPC ? 240 : 320, // 모바일에서 등장 지점을 더 올리기 (160 → 320)
			fadeInStartBottom: isPC ? 0 : 160, // 모바일에서만 160px부터 페이드인 시작
			targetScale: 0.5,
			targetY: 0,
			targetOpacity: isPC ? 0.3 : 0,
			enableOpacityMapping: true,
		}),
		[isPC]
	);
	const [currentSection, setCurrentSection] = useState(0);
	const [isVideoLoaded, setIsVideoLoaded] = useState(false);
	const [hasAutoPlayStarted, setHasAutoPlayStarted] = useState(false);
	const [autoPlayCompleted, setAutoPlayCompleted] = useState(false);
	const [scrollProgress, setScrollProgress] = useState(0);
	// 비디오 상태 추가
	const [videoError, setVideoError] = useState(null);
	const [videoLoadingState, setVideoLoadingState] = useState("loading");
	// 상위 컨테이너 크기 및 위치 상태
	const [containerDimensions, setContainerDimensions] = useState({
		width: 0,
		height: 0,
		left: 0,
		top: 0,
	});
	// 비디오 실제 크기 상태
	const [videoDimensions, setVideoDimensions] = useState({
		width: 0,
		height: 0,
		top: 0,
		left: 0,
		containerHeight: 0,
		containerTop: 0,
	});
	// 위치 감지 상태
	const [isAtTargetPosition, setIsAtTargetPosition] = useState(false);

	// 타이틀 스크롤 상태 계산 (컴포넌트 레벨에서 계산)
	const scrollThreshold = isPC ? 0.08 : 0.05;
	const isTitleScrolled = scrollProgress > scrollThreshold;

	// 자동재생 제거됨 - 처음부터 스크롤 매핑 활성화
	useEffect(() => {
		if (!isVideoLoaded) return;
		setAutoPlayCompleted(true); // 즉시 스크롤 매핑 활성화
	}, [isVideoLoaded]);

	// 🔧 컨테이너 크기 측정 비활성화 (스크롤 문제 방지)
	// useEffect(() => {
	// 	updateContainerDimensions();
	// }, []);

	// 윈도우 리사이즈는 비활성화 유지 (스크롤 간섭 방지)
	// useEffect(() => {
	// 	const handleResize = () => {
	// 		updateContainerDimensions();
	// 		if (isVideoLoaded) {
	// 			setTimeout(updateVideoDimensions, 50);
	// 		}
	// 	};
	// 	window.addEventListener("resize", handleResize);
	// 	return () => window.removeEventListener("resize", handleResize);
	// }, [isVideoLoaded]);

	// (스크롤 이벤트 useEffect는 함수들 정의 후 아래에 배치)

	// 상위 컨테이너 크기 및 위치 측정 함수 (메모이제이션)
	const updateContainerDimensions = useCallback(() => {
		if (!containerRef.current) return;

		const containerRect = containerRef.current.getBoundingClientRect();
		const newDimensions = {
			width: containerRect.width,
			height: containerRect.height,
			left: containerRect.left,
			top: containerRect.top,
		};
		
		// 값이 실제로 변경되었을 때만 상태 업데이트 (불필요한 리렌더링 방지)
		setContainerDimensions(prev => {
			if (prev.width !== newDimensions.width || 
				prev.height !== newDimensions.height ||
				Math.abs(prev.left - newDimensions.left) > 1 ||
				Math.abs(prev.top - newDimensions.top) > 1) {
				return newDimensions;
			}
			return prev;
		});
	}, []);

	// 위치 감지 함수: 스크롤 95% 이후 absolute 전환 (메모이제이션)
	const checkTargetPosition = useCallback(() => {
		const shouldTransition = scrollProgress >= 0.95;
		setIsAtTargetPosition(prev => prev !== shouldTransition ? shouldTransition : prev);
		return shouldTransition;
	}, [scrollProgress]);

	// 🎯 비디오 길이/FPS 독립적 시간 매핑 함수 (메모이제이션)
	const getVideoTimeFromProgress = useCallback((progress, videoElement) => {
		if (
			!videoElement ||
			!videoElement.duration ||
			isNaN(videoElement.duration)
		) {
			return 0;
		}

		// 구간 기반 매핑: sections 배열을 기준으로 균등 분할
		const sectionCount = Math.max(1, sections.length);
		const sectionProgress = progress * sectionCount;
		const currentSectionIndex = Math.floor(sectionProgress);
		const withinSectionProgress = sectionProgress - currentSectionIndex;

		// 각 섹션이 비디오 전체를 균등 분할
		const sectionStartTime =
			(currentSectionIndex / sectionCount) * videoElement.duration;
		const sectionEndTime =
			((currentSectionIndex + 1) / sectionCount) * videoElement.duration;
		const finalTime =
			sectionStartTime +
			withinSectionProgress * (sectionEndTime - sectionStartTime);

		// 안전한 범위 내에서 반환
		return Math.max(0, Math.min(finalTime, videoElement.duration - 0.05));
	}, [sections.length]);

	// 🎯 비디오 스크러빙 준비 상태 체크 함수 (메모이제이션)
	const isVideoReadyForScrubbing = useCallback((targetTime) => {
		if (!videoRef.current || !isVideoReady) return false;
		if (isNaN(targetTime) || targetTime < 0) return false;
		
		// 비디오 데이터 준비 상태 확인 (최소 HAVE_FUTURE_DATA 필요)
		if (videoRef.current.readyState < 3) return false;
		
		// duration 유효성 확인
		if (!videoRef.current.duration || isNaN(videoRef.current.duration)) return false;
		
		// seekable 범위 확인
		const seekable = videoRef.current.seekable;
		if (seekable.length === 0) return false;
		
		// targetTime이 seekable 범위 내에 있는지 확인
		const seekableStart = seekable.start(0);
		const seekableEnd = seekable.end(seekable.length - 1);
		if (targetTime < seekableStart || targetTime > seekableEnd) return false;
		
		return true;
	}, [isVideoReady]);

	// 🎯 개선된 부드러운 비디오 시간 전환 함수 (메모이제이션)
	const smoothVideoSeek = useCallback((targetTime) => {
		if (!videoRef.current) return;
		
		// 비디오가 seeking 중이면 스킵 (브라우저 최적화)
		if (videoRef.current.seeking) return;

		const currentTime = videoRef.current.currentTime;
		const timeDiff = Math.abs(targetTime - currentTime);
		
		// 너무 작은 차이는 무시 (불필요한 seeking 방지)
		if (timeDiff < 0.05) return;

		// 스크롤 속도에 따른 동적 보간율 계산
		const getInterpolationRate = (diff) => {
			if (diff < 0.1) return 1; // 즉시 적용
			if (diff < 0.3) return 0.6; // 빠른 보간  
			if (diff < 0.8) return 0.4; // 중간 보간
			if (diff < 2.0) return 0.2; // 느린 보간
			return 1; // 큰 점프는 즉시 적용
		};

		const interpolationRate = getInterpolationRate(timeDiff);
		
		if (interpolationRate === 1) {
			// 즉시 적용
			videoRef.current.currentTime = targetTime;
		} else {
			// 동적 보간 적용
			const interpolatedTime = currentTime + (targetTime - currentTime) * interpolationRate;
			videoRef.current.currentTime = interpolatedTime;
		}
	}, []);

	// 비디오 실제 크기 계산 함수
	const updateVideoDimensions = () => {
		if (!videoRef.current || !containerRef.current) return;

		const video = videoRef.current;
		const videoRect = video.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// 컨테이너 높이를 비디오 높이에 맞춤
		const containerHeight = videoRect.height;

		// 컨테이너를 화면 중앙에 배치하기 위한 top 계산
		const containerTop = (viewportHeight - containerHeight) / 2;

		setVideoDimensions({
			width: videoRect.width,
			height: videoRect.height,
			top: 0, // 컨테이너 내부에서는 0으로 시작
			left: 0, // 컨테이너 내부에서는 0으로 시작
			containerHeight: containerHeight,
			containerTop: containerTop,
		});
	};

	// 🎯 비디오 메타데이터 로드 핸들러 (duration 정보 우선 획득)
	const handleLoadedMetadata = () => {
		if (videoRef.current && videoRef.current.duration) {
			const videoDuration = videoRef.current.duration;
			console.log(`📊 Video metadata loaded - Duration: ${videoDuration}s`);

			// 실제 duration이 설정값과 다르면 즉시 업데이트
			if (Math.abs(videoDuration - configDuration) > 0.05) {
				console.log(
					`🔄 Early duration update: ${configDuration}s -> ${videoDuration}s`
				);
				setActualDuration(videoDuration);
			}
		}
	};

	// 비디오 로드 완료 핸들러
	const handleVideoLoaded = () => {
		console.log("📹 Video loaded successfully:", videoSrc);
		setIsVideoLoaded(true);
		setVideoLoadingState("loaded");
		setVideoError(null);
		setRetryCount(0); // 성공 시 재시도 카운트 리셋

		// 🎯 실제 비디오 duration 읽기 (압축된 비디오 대응)
		if (videoRef.current && videoRef.current.duration) {
			const videoDuration = videoRef.current.duration;
			console.log(
				`🎯 Video duration: config=${configDuration}s, actual=${videoDuration}s`
			);

			// 실제 duration이 설정값과 다르면 업데이트
			if (Math.abs(videoDuration - configDuration) > 0.1) {
				console.log(
					`⚡ Updating duration from ${configDuration}s to ${videoDuration}s`
				);
				setActualDuration(videoDuration);
			}

			setIsVideoReady(true);
		}

		// 컨테이너와 비디오 크기 업데이트
		setTimeout(() => {
			updateContainerDimensions();
			updateVideoDimensions();
		}, 100);
	};

	// 네트워크 연결 상태 확인
	const checkNetworkAndRetry = () => {
		if ("onLine" in navigator && !navigator.onLine) {
			console.log("📡 Network offline detected");
			return false;
		}
		return true;
	};

	// 비디오 로드 에러 핸들러
	const handleVideoError = (error) => {
		console.error(
			"❌ Video load error:",
			error,
			"Video src:",
			videoSrc,
			"Retry count:",
			retryCount
		);

		// 무한 루프 방지: 최대 재시도 횟수 초과 시 중단
		if (retryCount >= maxRetries) {
			console.error("❌ Max retries exceeded. Stopping video load attempts.");
			setVideoLoadingState("failed");
			setVideoError(error.target?.error || error);
			return;
		}

		setVideoLoadingState("error");
		setVideoError(error.target?.error || error);
		setRetryCount((prev) => prev + 1);

		// 네트워크 상태 확인
		if (!checkNetworkAndRetry()) {
			console.log("⚠️ Network offline, will retry when online");
			return;
		}

		// Firebase Storage - 저화질 버전들만 사용 (빠른 로딩)
		const fallbackUrls = [
			"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fwriting_man_landing_low.mp4?alt=media", // 저화질 버전
			"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fwriting_man_landing_optimized.mp4?alt=media", // 최적화 버전 (백업)
			"/videos/writing_man_landing.mp4", // 로컬 백업
		];

		// 현재 URL과 다른 다음 URL 찾기
		let nextUrl = null;
		const currentUrl = videoRef.current?.src || videoSrc;

		for (const url of fallbackUrls) {
			if (!currentUrl.includes(url.split("/").pop())) {
				nextUrl = url;
				break;
			}
		}

		if (nextUrl && videoRef.current) {
			console.log(
				`🔄 Retry ${retryCount + 1}/${maxRetries}: Trying URL:`,
				nextUrl
			);
			setVideoLoadingState("retrying");

			// 짧은 지연 후 재시도 (무한 루프 방지)
			setTimeout(() => {
				if (videoRef.current) {
					setVideoSrc(nextUrl);
					videoRef.current.src = nextUrl;
				}
			}, 1000);
		} else {
			console.error("❌ No more fallback URLs available");
			setVideoLoadingState("failed");
		}
	};

	// 비디오 로딩 시작 핸들러
	const handleLoadStart = () => {
		console.log("🚀 Video loading started:", videoSrc);
		setVideoLoadingState("loading");
		// 새로운 로딩 시작 시에는 재시도 카운트 리셋하지 않음 (재시도 중일 수 있음)
	};

	// 🎯 UspTemplate 구간 감지 및 스크롤 일시정지
	const [isInUspSection, setIsInUspSection] = useState(false);
	
	useEffect(() => {
		const checkUspSection = () => {
			const uspElement = document.querySelector('[data-usp-template]');
			if (uspElement) {
				const rect = uspElement.getBoundingClientRect();
				const windowHeight = window.innerHeight;
				
				// UspTemplate이 화면에 50% 이상 보이면 스크롤 동기화 중단
				const isVisible = rect.top < windowHeight * 0.5 && rect.bottom > windowHeight * 0.5;
				setIsInUspSection(isVisible);
			}
		};
		
		// 초기 체크
		checkUspSection();
		
		// 스크롤 시 체크 (throttle)
		let throttleTimer = null;
		const throttledCheck = () => {
			if (throttleTimer) return;
			throttleTimer = setTimeout(() => {
				checkUspSection();
				throttleTimer = null;
			}, 100);
		};
		
		window.addEventListener('scroll', throttledCheck, { passive: true });
		return () => {
			window.removeEventListener('scroll', throttledCheck);
			if (throttleTimer) clearTimeout(throttleTimer);
		};
	}, []);

	// 🎯 최적화된 스크롤 기반 비디오 동기화 - UspTemplate 구간에서 비활성화
	useEffect(() => {
		if (!autoPlayCompleted || isInUspSection) return; // UspTemplate 구간에서는 스크롤 동기화 중단

		let rafId = null;
		let lastScrollTop = 0;
		let lastContainerUpdate = 0; // 마지막 컨테이너 업데이트 시간

		// Lenis와 native scroll 호환 핸들러
		const handleScrollEvent = (scrollTop) => {
			// requestAnimationFrame으로 스로틀링
			if (rafId) return;

			rafId = requestAnimationFrame(() => {
				if (!videoRef.current || !isVideoLoaded) {
					rafId = null;
					return;
				}
				const now = Date.now();

				// 스크롤이 변경되지 않았으면 처리 스킵
				if (Math.abs(scrollTop - lastScrollTop) < 1) {
					rafId = null;
					return;
				}
				lastScrollTop = scrollTop;

				// 컨테이너 업데이트 최적화: 100ms마다 또는 큰 스크롤 변화시에만
				const shouldUpdateContainer = (now - lastContainerUpdate > 100) || 
											  (Math.abs(scrollTop - lastScrollTop) > 100);
				if (shouldUpdateContainer) {
					updateContainerDimensions();
					lastContainerUpdate = now;
				}

				// 위치 감지 확인
				checkTargetPosition();

				// 전체 문서 기준 스크롤 진행도 (비디오 위치 트랜지션용)
				const documentHeight =
					document.documentElement.scrollHeight - window.innerHeight;
				const globalProgress = scrollTop / documentHeight;
				setScrollProgress(globalProgress);

				// 컨테이너 진행도 변수를 먼저 선언
				let containerProgress = 0;

				// 컨테이너 기준 비디오 재생 타임라인 매핑
				if (containerRef.current) {
					const containerRect = containerRef.current.getBoundingClientRect();
					const containerTop = containerRect.top + scrollTop;

					// 🎯 스크롤 1부터 비디오 시작, 마지막 메시지까지 매핑
					const videoStartScrollY = 1; // 페이지 로딩 후 스크롤 1부터 시작
					const lastSectionIndex = sections.length - 1;
					const videoEndScrollY =
						containerTop +
						(lastSectionIndex *
							responsiveConfig.section.spacing *
							window.innerHeight) /
							100; // 마지막 메시지 시작점

					// 전체 매핑 구간 길이
					const totalMappingDistance = videoEndScrollY - videoStartScrollY;

					// 현재 스크롤 위치가 매핑 구간 내에서 어느 지점인지 계산
					const effectiveScrollDistance = scrollTop - videoStartScrollY;

					containerProgress = Math.max(
						0,
						Math.min(1, effectiveScrollDistance / totalMappingDistance)
					);

					// 🎯 정규화된 구간 기반 매핑 (비디오 길이/FPS 독립적)
					const normalizedProgress = Math.max(
						0,
						Math.min(1, containerProgress)
					);
					const targetTime = getVideoTimeFromProgress(
						normalizedProgress,
						videoRef.current
					);

					// 부드러운 시간 전환으로 자연스러운 스크러빙
					try {
						if (isVideoReadyForScrubbing(targetTime)) {
							smoothVideoSeek(targetTime);
						}
					} catch (error) {
						console.warn("⚠️ Video seek error:", error);
					}
				} else {
					// 컨테이너가 없을 경우 fallback
					containerProgress = globalProgress;
					const normalizedProgress = Math.max(0, Math.min(1, globalProgress));
					const targetTime = getVideoTimeFromProgress(
						normalizedProgress,
						videoRef.current
					);

					try {
						if (isVideoReadyForScrubbing(targetTime)) {
							smoothVideoSeek(targetTime);
						}
					} catch (error) {
						console.warn("⚠️ Video seek error:", error);
					}
				}

				// 현재 섹션 계산 - 컨테이너 기준으로 통일
				const sectionProgress = containerProgress; // 이제 항상 containerProgress 사용
				const sectionIndex = Math.floor(sectionProgress * sections.length);
				const clampedSection = Math.min(sectionIndex, sections.length - 1);

				if (clampedSection !== currentSection) {
					setCurrentSection(clampedSection);
					// onSectionChange prop이 제거되었으므로 내부에서 처리
				}

				rafId = null;
			});
		};

		// Lenis가 활성화되어 있으면 Lenis 이벤트 사용, 없으면 native scroll 사용
		if (window.lenis) {
			// Lenis scroll 이벤트 - scroll position을 직접 전달
			const handleLenisScroll = (e) => {
				handleScrollEvent(e.scroll);
			};
			
			window.lenis.on('scroll', handleLenisScroll);
			return () => {
				window.lenis.off('scroll', handleLenisScroll);
				if (rafId) {
					cancelAnimationFrame(rafId);
				}
			};
		} else {
			// Native scroll fallback
			const handleNativeScroll = () => {
				handleScrollEvent(window.pageYOffset);
			};
			
			window.addEventListener("scroll", handleNativeScroll, { passive: true });
			return () => {
				window.removeEventListener("scroll", handleNativeScroll);
				if (rafId) {
					cancelAnimationFrame(rafId);
				}
			};
		}
	}, [
		autoPlayCompleted,
		isInUspSection, // UspSection 상태 의존성 추가
		sections.length,
		currentSection,
		isVideoLoaded,
		isVideoReady,
		checkTargetPosition,
		getVideoTimeFromProgress,
		updateContainerDimensions,
		isVideoReadyForScrubbing,
		smoothVideoSeek,
		responsiveConfig.section.spacing,
	]);

	return (
		<Box
			ref={containerRef}
			sx={{
				position: "relative",
				width: "100%",
				// 컨테이너 높이 고정 (static 전환 방식 사용)
				height: `${sections.length * responsiveConfig.section.spacing}vh`, // 반응형 높이 적용
				overflow: "visible",
				zIndex: 1, // 전체 컨테이너 zIndex 설정
			}}
		>
			{/* 동적 비디오 영역 - 부드러운 위치 트랜지션 (사이즈 고정) */}
			<Box
				ref={videoContainerRef}
				sx={(() => {
					// 타이틀 스크롤 상태는 컴포넌트 레벨에서 계산됨

					// 3번째 메시지가 뷰포트 상단 1/4 지점보다 위로 올라갔는지 확인
					// 3번째 메시지 위치: 3 * spacing = 3 * 50vh = 150vh
					// 뷰포트 상단 1/4 지점: 스크롤 위치 + 25vh
					let shouldMoveToCenter = false;
					if (containerRef.current) {
						const scrollTop = window.pageYOffset;
						const containerRect = containerRef.current.getBoundingClientRect();
						const containerTop = containerRect.top + scrollTop;

						// 3번째 메시지 실제 위치 계산
						const thirdMessageTop =
							containerTop +
							(3 * responsiveConfig.section.spacing * window.innerHeight) / 100;
						const viewportQuarter = scrollTop + window.innerHeight / 4; // 뷰포트 상단 1/4 지점

						// 3번째 메시지가 뷰포트 상단 1/4 지점보다 위로 올라갔는지 확인
						shouldMoveToCenter = thirdMessageTop < viewportQuarter;
					}

					// 95% 이후 absolute 전환
					const shouldUseAbsolute = isAtTargetPosition;
					const position = shouldUseAbsolute ? "absolute" : "fixed";

					// 위치 설정 - 단위 일관성 유지로 트랜지션 보장
					let finalLeft, finalTop, finalBottom, finalTransform, finalOpacity;

					// PC에서 우측 위치 계산 (공통 사용)
					const finalLeftPercent =
						containerDimensions.width > 0
							? ((containerDimensions.left +
									containerDimensions.width * 0.725) /
									window.innerWidth) *
							  100
							: 72.5;

					if (isMobile) {
						// 🔥 모바일에서는 하단에 딱 붙이고 뷰포트 너비와 동일하게
						if (shouldUseAbsolute) {
							// 95% 이후 absolute로 전환하며 페이드아웃
							finalLeft = "0";
							finalTop = "auto";
							finalBottom = "-48px"; // 하단에서 -48px
							finalTransform = "translateX(0)";
							finalOpacity = 0; // 페이드아웃
						} else if (shouldMoveToCenter) {
							// 3번째 메시지 이후 페이드아웃
							finalLeft = "0";
							finalTop = "auto";
							finalBottom = "-48px"; // 하단에서 -48px
							finalTransform = "translateX(0)";
							finalOpacity = 0; // 페이드아웃
						} else {
							// 일반 상태 - 하단에서 -48px
							finalLeft = "0";
							finalTop = "auto";
							finalBottom = "-48px"; // 하단에서 -48px
							finalTransform = "translateX(0)";
							finalOpacity = 1; // 보이기
						}
					} else if (shouldUseAbsolute) {
						// PC - position: absolute일 때는 컨테이너 기준 하단 중앙에 배치
						finalLeft = "50%";
						finalTop = "calc(100% - 60vh)";
						finalBottom = "auto";
						finalTransform = "translate(-50%, -50%)";
						finalOpacity = 0;
					} else if (shouldMoveToCenter) {
						// PC - 3번째 메시지가 뷰포트 중앙 위로 올라갔을 때 중앙으로 이동하며 페이드아웃
						finalLeft = "50%";
						finalTop = "50%";
						finalBottom = "auto";
						finalTransform = "translate(-50%, -50%)";
						finalOpacity = 0;
					} else if (isTitleScrolled) {
						// PC - 타이틀 스크롤되면 우측으로 이동
						finalLeft = `${finalLeftPercent}%`;
						finalTop = "50%";
						finalBottom = "auto";
						finalTransform = "translate(-50%, -50%)";
						finalOpacity = 1;
					} else {
						// PC - 초기 위치 (하단 중앙, 큰 스케일)
						finalLeft = "50%";
						finalTop = "85%";
						finalBottom = "auto";
						finalTransform = "translate(-50%, -50%) scale(2)";
						finalOpacity = 1;
					}

					return {
						position: position,
						left: finalLeft,
						top: finalTop,
						bottom: finalBottom,
						transform: finalTransform,
						opacity: finalOpacity,
						width: isPC
							? containerDimensions.width > 0
								? `${containerDimensions.width * 0.55}px`
								: "55%"
							: "100vw", // 모바일에서 뷰포트 전체 너비
						height: isPC
							? containerDimensions.width > 0 &&
							  videoRef.current?.videoWidth &&
							  videoRef.current?.videoHeight
								? `${
										containerDimensions.width *
										0.55 *
										(videoRef.current.videoHeight / videoRef.current.videoWidth)
								  }px`
								: "auto"
							: "auto", // 모바일에서 항상 auto 높이
						zIndex: shouldUseAbsolute ? 1 : isMobile ? 3 : 2, // 모바일에서 메시지 텍스트(zIndex:4)보다 아래
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: isMobile ? "none" : `0.5px solid rgba(122, 133, 145, ${
							isTitleScrolled ? 1 : 0
						})`, // 모바일에서는 border 제거
						borderRadius: isMobile ? "48px" : "16px", // 모바일에서는 48px
						overflow: "hidden",
						boxSizing: "border-box",
						// CSS transition 적용 - delay 제거, 빠른 반응
						transition: "all 0.3s ease-out",
						// 마우스 이벤트 방지 (텍스트 선택 등 방지)
						pointerEvents: "none",
						userSelect: "none",
					};
				})()}
			>
				<video
					ref={videoRef}
					muted
					playsInline
					preload="metadata" // AWS Amplify에서 안정성을 위해 metadata만 로드
					loading="lazy" // 초기 로딩 부담 감소
					onLoadedData={handleVideoLoaded}
					onError={handleVideoError}
					onLoadStart={handleLoadStart}
					onLoadedMetadata={handleLoadedMetadata}
					style={{
						width: "100%",
						height: "auto", // 항상 auto로 비율 유지
						maxHeight: isPC ? "76vh" : "60vh", // 최대 높이만 제한
						objectFit: "contain", // 비율 유지
						borderRadius: isMobile ? "48px" : "12px", // 모바일에서는 48px
						pointerEvents: "none", // 비디오 자체도 마우스 이벤트 방지
						userSelect: "none",
						// 비디오에도 동일한 blur 효과 적용
						filter: (() => {
							const transitionProgress =
								scrollProgress <= 0.25 ? scrollProgress / 0.25 : 1;
							const initialBlur = 1; // 비디오는 컨테이너보다 약간 적게
							const currentBlur = initialBlur * (1 - transitionProgress);
							return currentBlur > 0 ? `blur(${currentBlur}px)` : "none";
						})(),
					}}
					// 로딩 최적화를 위한 속성들
					crossOrigin="anonymous"
					controlsList="nodownload"
					disablePictureInPicture
					poster="" // 포스터 이미지 없음으로 빠른 로딩
				>
					<source src={videoSrc} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			</Box>

			{/* 스크롤 가능한 텍스트 영역 */}
			<Box
				sx={{
					position: "absolute",
					left: 0,
					top: 0,
					width: "100%", // 전체 너비
					height: "100%",
					zIndex: 3, // 텍스트 컨테이너
				}}
			>
				{sections.map((section, index) => (
					<Box
						key={section.id}
						sx={{
							position: "absolute",
							top: `${index * responsiveConfig.section.spacing}vh`,
							left: 0,
							// 섹션 0(타이틀)은 전체 화면, 나머지는 반응형 너비 (PC: 45%, Mobile: 100%)
							width:
								index === 0
									? "100%"
									: containerDimensions.width > 0
									? `${
											containerDimensions.width *
											responsiveConfig.message.sectionWidth
									  }px`
									: `${responsiveConfig.message.sectionWidth * 100}%`,
							height:
								index === 0
									? responsiveConfig.section.titleHeight
									: responsiveConfig.section.contentHeight,
							display: "flex",
							alignItems: "center",
							justifyContent: index === 0 ? "center" : "flex-start", // 타이틀은 중앙 정렬
							zIndex: 4,
							boxSizing: "border-box",
							overflow: "visible", // 텍스트 overflow 허용
						}}
					>
						<BrandMessageSection
							section={section}
							index={index}
							currentSection={currentSection}
							scrollProgress={scrollProgress}
							shrinkingScrollOptions={shrinkingScrollOptions}
						/>
					</Box>
				))}
			</Box>

			{/* 로딩 상태 - 은은한 그라데이션 애니메이션 */}
			{!isVideoLoaded && (
				<Box
					sx={{
						position: videoContainerRef.current ? "absolute" : "fixed",
						top: "50%",
						left: "50%",
						width: isPC
							? containerDimensions.width > 0
								? `${containerDimensions.width * 0.55}px`
								: "55%"
							: "calc(100% - 48px)", // 모바일에서 원래 비디오 크기와 동일
						height: isPC
							? containerDimensions.width > 0 &&
							  videoRef.current?.videoWidth &&
							  videoRef.current?.videoHeight
								? `${
										containerDimensions.width *
										0.55 *
										(videoRef.current.videoHeight / videoRef.current.videoWidth)
								  }px`
								: "auto"
							: "auto", // 모바일에서 비율 유지
						transform: "translate(-50%, -50%)",
						borderRadius: isPC ? "16px" : "16px",
						zIndex: 3,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						overflow: "hidden",
						// 은은한 그라데이션 애니메이션
						background: `
              linear-gradient(135deg, 
                rgba(248, 250, 252, 0.8), 
                rgba(241, 245, 249, 0.8), 
                rgba(235, 241, 247, 0.8),
                rgba(248, 250, 252, 0.8)
              )
            `,
						backgroundSize: "400% 400%",
						animation: "subtleGradientFlow 4s ease-in-out infinite",
						backdropFilter: "blur(2px)",
						"@keyframes subtleGradientFlow": {
							"0%, 100%": {
								backgroundPosition: "0% 50%",
							},
							"50%": {
								backgroundPosition: "100% 50%",
							},
						},
					}}
				/>
			)}
		</Box>
	);
}

/**
 * 언어별 키워드 강조 렌더링
 * HighlightedText 컴포넌트를 사용한 모듈화된 방식
 */
function renderHighlightedText(sectionIndex, text, language) {
	// 한국어 버전
	if (language === "ko") {
		switch (sectionIndex) {
			case 0: // "오늘도 Threads에 어떤글을 올릴지 고민하고 게신가요?"
				return (
					<HighlightedText>
						오늘도 <HighlightedText.Bold>Threads</HighlightedText.Bold>에{" "}
						<HighlightedText.Bold>어떤글을</HighlightedText.Bold>
						<HighlightedText.Break />
						올릴지 <HighlightedText.Bold>고민</HighlightedText.Bold>하고
						계신가요?
					</HighlightedText>
				);
			case 1: // "MTI는 여러분의 Threads를 아이디어 노트로 바꿔드립니다."
				return (
					<HighlightedText>
						<HighlightedText.Bold>MTI</HighlightedText.Bold>는 여러분의{" "}
						<HighlightedText.Bold>Threads</HighlightedText.Bold>를
						<HighlightedText.Break />
						<HighlightedText.Bold>아이디어 노트</HighlightedText.Bold>로
						바꿔드립니다.
					</HighlightedText>
				);
			case 2: // "자기만의 주제와 쓰기 루틴으로 '나'라는 브랜드 채워나가세요."
				return (
					<HighlightedText>
						나만의 <HighlightedText.Bold>주제와</HighlightedText.Bold> 글쓰기{" "}
						<HighlightedText.Bold>루틴</HighlightedText.Bold>으로
						<HighlightedText.Break />
						<HighlightedText.Bold>'나'</HighlightedText.Bold>라는{" "}
						<HighlightedText.Bold>브랜드</HighlightedText.Bold>를 채워나가세요.
					</HighlightedText>
				);
			default:
				return <HighlightedText>{text}</HighlightedText>;
		}
	}

	// 영어 버전
	if (language === "en") {
		switch (sectionIndex) {
			case 0: // "Still wondering what to post on Threads today?"
				return (
					<HighlightedText>
						Still <HighlightedText.Bold>wondering</HighlightedText.Bold> what to{" "}
						<HighlightedText.Bold>post</HighlightedText.Bold>
						<HighlightedText.Break />
						on <HighlightedText.Bold>Threads</HighlightedText.Bold> today?
					</HighlightedText>
				);
			case 1: // "MTI turns your Threads into idea notes."
				return (
					<HighlightedText>
						<HighlightedText.Bold>MTI</HighlightedText.Bold> turns your{" "}
						<HighlightedText.Bold>Threads</HighlightedText.Bold>
						<HighlightedText.Break />
						into <HighlightedText.Bold>idea notes</HighlightedText.Bold>.
					</HighlightedText>
				);
			case 2: // "Build your own brand with unique topics and routines."
				return (
					<HighlightedText>
						Build <HighlightedText.Bold>your own</HighlightedText.Bold>{" "}
						<HighlightedText.Bold>brand</HighlightedText.Bold> with
						<HighlightedText.Break />
						<HighlightedText.Bold>unique topics</HighlightedText.Bold> and{" "}
						<HighlightedText.Bold>routines</HighlightedText.Bold>.
					</HighlightedText>
				);
			default:
				return <HighlightedText>{text}</HighlightedText>;
		}
	}

	// 기본값 (원본 텍스트)
	return <HighlightedText>{text}</HighlightedText>;
}

/**
 * 개별 브랜드 메시지 섹션 컴포넌트
 */
const BrandMessageSection = memo(
	({
		section,
		index,
		currentSection,
		scrollProgress,
		shrinkingScrollOptions,
	}) => {
		const { i18n } = useTranslation();
		const { isPC, isMobile, isMobileOnly } = useResponsive();
		const isActive = currentSection === index; // 스크롤에 따라 섹션 변경

		// 반응형 설정 (BrandMessageSection 내부)
		const responsiveConfig = useMemo(
			() => ({
				text: {
					marginTop: isPC ? "-25vh" : "-15vh",
					width: isPC ? "120%" : "95%", // PC: 영상과 살짝 겹치게, Mobile: 디바이스 크기 벗어나지 않게
					marginTopTitle: isPC ? "-24vh" : "-60vh", // 모바일에서 타이틀을 더욱 위로 이동
				},
				typography: {
					mainTitle: isMobileOnly ? "h5" : "h3", // 모바일에서 한 사이즈 크게 (h6 → h5)
					subtitle: isMobileOnly ? "body1" : "h6", // 모바일에서 한 사이즈 크게 (caption → body1)
				},
			}),
			[isPC, isMobileOnly]
		);

		// Typography 설정 객체화 (메모이제이션)
		const typographyConfig = useMemo(
			() => ({
				mainTitle: {
					variant: responsiveConfig.typography.mainTitle,
					sx: {
						fontWeight: isMobileOnly ? 400 : 300, // 실제 모바일에서만 더 진한 폰트 웨이트
						mb: isMobileOnly ? 1.5 : 3, // 360px에서 간격 최적화
						whiteSpace: "pre-line",
						wordBreak: "normal",
						lineHeight: isMobileOnly ? 1.25 : 1.4, // 360px에서 더 타이트하게 조정
						letterSpacing: isMobileOnly ? "-0.005em" : "0.01em", // 실제 모바일에서만 더 타이트한 자간
						overflow: "visible",
						// 실제 모바일에서만 하드코딩된 폰트 크기 (25px)
						fontSize: isMobileOnly ? "25px" : undefined, // 모바일에서 25px로 하드코딩
						textAlign: isMobileOnly ? "center" : "left", // 모바일에서 중앙 정렬
					},
				},
				subtitle: {
					variant: responsiveConfig.typography.subtitle,
					sx: {
						fontWeight: isMobileOnly ? 300 : 200, // 실제 모바일에서만 더 진한 폰트 웨이트
						wordBreak: "keep-all",
						lineHeight: isMobileOnly ? 1.3 : 1.5, // 360px에서 더 타이트하게 조정
						letterSpacing: isMobileOnly ? "0.005em" : "0.01em", // 실제 모바일에서만 약간 조정
						opacity: 0.8,
						// 실제 모바일에서만 더 적절한 폰트 크기 (360px 기준 최적화)
						fontSize: isMobileOnly ? "1rem" : undefined, // 모바일에서 더 크게 조정
						textAlign: isMobileOnly ? "center" : "left", // 모바일에서 중앙 정렬
					},
				},
			}),
			[
				responsiveConfig.typography.mainTitle,
				responsiveConfig.typography.subtitle,
				isMobileOnly,
			]
		);

		// 타이틀 트랜지션 구간(섹션 0)에서는 타이틀 컴포넌트 렌더링
		if (index === 0) {
			return (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-start", // 원래대로 복원
						alignItems: "center",
						textAlign: "center",
						position: "relative",
						zIndex: 20,
						marginTop: responsiveConfig.text.marginTopTitle, // 약간 위로 조정
					}}
				>
					<ShrinkingScrollExit {...shrinkingScrollOptions}>
						<Box sx={{ position: "relative", marginTop: isMobile ? "-8vh" : "0vh" }}>
							<LoginTopSection scrollProgress={scrollProgress} spacing={2.5} />
						</Box>
					</ShrinkingScrollExit>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					textAlign: isMobileOnly ? "center" : "left", // 모바일에서 중앙 정렬
					color: "#333333",
					position: "relative",
					zIndex: 20,
					overflow: "visible", // 텍스트 overflow 허용
				}}
			>
				<Box
					sx={{
						marginTop: responsiveConfig.text.marginTop,
						width: responsiveConfig.text.width, // 텍스트 영역을 반응형으로 조정
					}}
				>
					{" "}
					{/* 50vh에서 25vh로 조정 */}
					<ShrinkingScrollExit {...shrinkingScrollOptions}>
						{/* 메인 타이틀 - 객체화된 설정 사용 */}
						<Typography
							variant={typographyConfig.mainTitle.variant}
							sx={typographyConfig.mainTitle.sx}
						>
							{renderHighlightedText(index - 1, section?.title, i18n.language)}{" "}
							{/* index - 1로 조정 */}
						</Typography>

						{/* 부제목 (현재 사용하지 않음) */}
						{section?.subtitle && (
							<Typography
								variant={typographyConfig.subtitle.variant}
								sx={typographyConfig.subtitle.sx}
							>
								{section?.subtitle}
							</Typography>
						)}
					</ShrinkingScrollExit>
				</Box>
			</Box>
		);
	}
);

ScrubbingVideoWithFullPage.propTypes = {
	// videoSrc: PropTypes.string.isRequired, // 제거됨
	sections: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number,
			step: PropTypes.string,
			title: PropTypes.string,
			subtitle: PropTypes.string,
			textColor: PropTypes.string,
		})
	).isRequired,
	// duration: PropTypes.number, // 제거됨
	// onSectionChange: PropTypes.func, // 제거됨
};

BrandMessageSection.propTypes = {
	section: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	currentSection: PropTypes.number.isRequired,
	scrollProgress: PropTypes.number,
	shrinkingScrollOptions: PropTypes.object.isRequired,
};

export default ScrubbingVideoWithFullPage;
